import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {fetchLoggedUser, getApiInformation, requirePinCode as requirePinCodeAction, stopImpersonation} from '../../slices/auth.slice';
import Sidebar from './Sidebar';
import NavbarComponent from './NavbarComponent';
import PinCode from './PinCode';
import TwoFactorVerification from '../../components/TwoFactorVerification';
import ErrorBoundary from '../../components/ErrorBoundary';

import './Layout.scss';
import Loading from '../../components/Loading';
import {fetchOrganization} from '../../slices/organizations.slice';
import {setPlaying, clearPlaying, setControlLock} from '../../slices/play-movie.slice';
import {setOrderRealtimeStatus} from '../../slices/orders.slice';
import { getPlayingMovie } from '../../services/playback';
import MovieControl from '../../components/MovieControl';
import {fetchNotifications} from '../../slices/notifications.slice';
import { initializeSocket, disconnectSocket } from '../../services/socket';
import ImpersonationBanner from './ImpersonationBanner';
import { consumeExpiredNotice, isExpired, isImpersonating as isImpersonatingStored, isReverting } from '../../services/impersonation';


const ROLES_WITHOUT_PIN = ['root', 'bac-user', 'bac-admin'];

// Safety net for what the banner's own countdown can't see: a slept laptop, a throttled
// background tab, or another tab ending the session. The deadline is an absolute
// timestamp, so a late check only delays the revert — it can never extend the session.
const IMPERSONATION_CHECK_MS = 15000;

// Failsafe: a lost playback:lock unlock event (e.g. reconnect mid-action) can't
// freeze the controls forever; the server-side 409 guard stays authoritative.
const CONTROL_LOCK_TTL_MS = 60000;

const isAccessTokenExpired = (token) => {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload?.exp) return false;
        return payload.exp * 1000 <= Date.now();
    } catch {
        return true;
    }
};

const Layout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isImpersonating = useSelector((state) => state.auth.isImpersonating);
    const token = useSelector((state) => state.auth.token);
    const user = useSelector((state) => state.auth.user);
    const { organization } = useSelector(state => state.organizations);
    const { playing } = useSelector(state => state.playMovie);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const isLoading = useSelector((state) => state.auth.isLoading);
    const requirePinCode = useSelector((state) => state.auth.requirePinCode);
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    const toggleSidebar = () => {
        if (window.innerWidth < 1200) {
            setSidebarOpen(!isSidebarOpen);
        }
    };

    // Leaving impersonation is a full page load, not a route change: it is the one way to
    // guarantee no org-scoped state (the playback overlay, the organization, the socket,
    // requests still in flight) outlives the session it belonged to.
    const handleStopImpersonating = useCallback(async (reason = 'manual') => {
        const result = await dispatch(stopImpersonation({ reason }));
        window.location.assign(result.payload?.exitPath || '/');
    }, [dispatch]);

    useEffect(() => {
        dispatch(getApiInformation());
        if (user && user.parentOrganization) {
            dispatch(fetchOrganization({ orgId: user.parentOrganization._id }));
            // Fetch currently playing movie
            getPlayingMovie().then(result => {
                if (result.data) {
                    dispatch(setPlaying(result.data));
                }
            });
        }
    }, [dispatch, user]);

    // The impersonation deadline. Checked on a short interval and again whenever the tab
    // comes back — a laptop that slept through the deadline wakes up already reverted.
    // A `storage` event means another tab ended it, and this one reloads into whatever
    // session that tab left behind.
    useEffect(() => {
        if (!isImpersonating) return undefined;

        const check = () => {
            if (isReverting()) return;
            if (!isImpersonatingStored()) {
                // Another tab already reverted this browser; reload into the session it
                // left behind rather than keep acting as the impersonated user.
                window.location.reload();
            } else if (isExpired()) {
                handleStopImpersonating('expired');
            }
        };

        const interval = setInterval(check, IMPERSONATION_CHECK_MS);
        window.addEventListener('focus', check);
        window.addEventListener('storage', check);
        document.addEventListener('visibilitychange', check);
        check();

        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', check);
            window.removeEventListener('storage', check);
            document.removeEventListener('visibilitychange', check);
        };
    }, [isImpersonating, handleStopImpersonating]);

    // Set by whichever revert ran without a UI to speak through — the boot-time one, or
    // a revert followed by a reload.
    useEffect(() => {
        if (consumeExpiredNotice()) {
            toast.info('Impersonation expired — you are back on your own account.');
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            if (token) {
                const role = localStorage.getItem('role');
                const hasPinCode = localStorage.getItem('hasPinCode') === 'true';
                const refreshTokenLs = localStorage.getItem('refreshToken');
                const pinEligible = role && !ROLES_WITHOUT_PIN.includes(role) && hasPinCode && refreshTokenLs;

                // Check the stored token, not state.token: a PIN unlock on /login rotates
                // only localStorage (see refreshToken.fulfilled), so state.token is still
                // the expired one and would ask for the PIN a second time.
                if (pinEligible && isAccessTokenExpired(localStorage.getItem('token'))) {
                    dispatch(requirePinCodeAction());
                } else {
                    dispatch(fetchLoggedUser(token));
                    dispatch(fetchNotifications());
                }
            } else {
                navigate('/login');
            }

        }
    }, [token, dispatch, navigate, isAuthenticated]);

    const playingRef = useRef(playing);
    useEffect(() => { playingRef.current = playing; }, [playing]);

    // Global, order-agnostic realtime bridge: keeps every logged-in client's
    // player bar, order statuses and control locks in sync with the org's
    // playback events. Registered on the socket this effect creates so
    // token-refresh reconnects re-attach the handlers. `playing` is read via a
    // ref — adding it to the deps would tear the socket down on every change.
    useEffect(() => {
        if (!isAuthenticated || !token) return undefined;
        const socket = initializeSocket();
        if (!socket) return undefined;

        let lockTtl = null;
        let hasConnected = socket.connected;
        let fetching = false;
        let lastAttempt = { orderId: null, at: 0 };

        const syncPlayingFromServer = async (orderId) => {
            if (!orderId || fetching || playingRef.current?._id === orderId) return;
            // Cooldown: don't refetch-loop when getPlayingMovie legitimately
            // returns nothing for this user.
            if (lastAttempt.orderId === orderId && Date.now() - lastAttempt.at < 30000) return;
            fetching = true;
            lastAttempt = { orderId, at: Date.now() };
            try {
                const result = await getPlayingMovie();
                if (result.data) {
                    dispatch(setPlaying(result.data));
                }
            } finally {
                fetching = false;
            }
        };

        const applyLock = (lock) => {
            clearTimeout(lockTtl);
            if (lock) {
                lockTtl = setTimeout(() => dispatch(setControlLock(null)), CONTROL_LOCK_TTL_MS);
            }
            dispatch(setControlLock(lock));
        };

        const onStatus = (data) => {
            if (data.status === 'playing') {
                dispatch(setOrderRealtimeStatus({ orderId: data.orderId, status: 'playing' }));
                syncPlayingFromServer(data.orderId);
            } else if (data.status === 'stopped') {
                dispatch(clearPlaying());
                dispatch(setOrderRealtimeStatus({
                    orderId: data.orderId,
                    status: data.orderStatus || 'finished-playback',
                }));
                // Nobody in the room pressed stop — say why the bar vanished.
                if (data.reason === 'paused-timeout') {
                    toast.warning('Playback was paused for too long — the content has been ejected automatically.');
                }
            }
            // 'finished' stays with the bar's countdown; the follow-up eject
            // broadcasts 'stopped', which clears state everywhere.
        };

        const onCurrent = (data) => {
            if (data?.orderId) syncPlayingFromServer(data.orderId);
            if (data && 'lock' in data) applyLock(data.lock);
        };

        const onLock = (data) => {
            applyLock(data.locked ? { action: data.action, orderId: data.orderId } : null);
        };

        // Self-heal: position events for an order we aren't showing (e.g. the
        // 'playing' emit raced the /play transaction commit) trigger a fetch.
        const onPosition = (data) => {
            if (playingRef.current?._id !== data.orderId) syncPlayingFromServer(data.orderId);
        };

        // Events broadcast while disconnected are lost, and the server only
        // sends playback:current when something is live — so after a reconnect
        // re-read the truth rather than keep a bar for a show that ended (or
        // a lock that was released) in the gap. The server's playback:current
        // follows this event and re-applies any lock still held.
        const onConnect = async () => {
            if (!hasConnected) {
                hasConnected = true;
                return;
            }
            applyLock(null);
            const result = await getPlayingMovie();
            if (result.error) return;
            if (result.data) {
                dispatch(setPlaying(result.data));
            } else if (playingRef.current) {
                dispatch(clearPlaying());
            }
        };

        socket.on('connect', onConnect);
        socket.on('playback:status', onStatus);
        socket.on('playback:current', onCurrent);
        socket.on('playback:lock', onLock);
        socket.on('playback:position', onPosition);

        return () => {
            socket.off('connect', onConnect);
            socket.off('playback:status', onStatus);
            socket.off('playback:current', onCurrent);
            socket.off('playback:lock', onLock);
            socket.off('playback:position', onPosition);
            clearTimeout(lockTtl);
            disconnectSocket();
        };
    }, [isAuthenticated, token, dispatch]);

    if (isLoading) {
        return <Loading fullHeight />;
    }

    if (requirePinCode) {
        return <PinCode />;
    }

    return (
        <div className={`text-white layout-parent bg-broken position-relative${isImpersonating? ' impersonating' : ''}`}>
            <NavbarComponent isOpen={isSidebarOpen} toggle={toggleSidebar} />

            <div className="d-flex">
                <Sidebar isOpen={isSidebarOpen} toggle={toggleSidebar} organization={organization} />
                <div className={`main-content ${playing ? 'playing' : ''}`}>
                    <ErrorBoundary>
                        <Outlet/>
                    </ErrorBoundary>
                </div>
            </div>

            {playing && (
                <div className="player position-fixed w-100 h-100 top-0 start-0 d-flex flex-column p-5">
                    <MovieControl playing={playing} movieId={playing.movie._id} orderId={playing._id} />
                </div>
            )}

            {isImpersonating && user && (
                <ImpersonationBanner user={user} onStop={handleStopImpersonating} />
            )}
            
            <TwoFactorVerification />
        </div>
    );
};

export default Layout;
